import React, { useState, useEffect, useRef, useCallback } from "react";
import { ITaskController } from "../../controller";
import { TaskElement } from "../../schema";
import { handleTabInTextArea } from "frontend/canvas-designer-new/text-element/text-utils";
import useObservableController from "elements/hooks/use-observable-controller";

interface EditableTextAreaProps {
  controller: ITaskController;
  field: "title" | "description";
  placeholder: string;
  className?: string;
  autoResize?: boolean;
  onFieldChanged?: (field: string) => void;
}

export function EditableTextArea({
  controller,
  field,
  placeholder,
  className,
  autoResize = false,
  onFieldChanged,
}: EditableTextAreaProps): React.ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(controller.element[field] || "");
  const previousValueRef = useRef<string>(controller.element[field] || "");
  const isEditingRef = useRef<boolean>(false);
  const sequenceId = `field-${field}`;
  const debounceTimeoutRef = useRef<number>(0);
  const pendingUpdateRef = useRef<{ newValue: string; previousValue: string } | null>(null);
  const controllerRef = useRef(controller);
  const onFieldChangedRef = useRef(onFieldChanged);
  const fieldRef = useRef(field);

  // Keep refs up to date
  useEffect(() => {
    controllerRef.current = controller;
    onFieldChangedRef.current = onFieldChanged;
    fieldRef.current = field;
  }, [controller, onFieldChanged, field]);

  useObservableController(controller, `${field}-changed`);

  useEffect(() => {
    if (isEditingRef.current) return;
    const elementFieldValue = controller.element[field];
    const currentValue = elementFieldValue || "";
    setValue(currentValue);
    previousValueRef.current = currentValue;
  }, [field, controller.element]);

  const resizeTextArea = useCallback(() => {
    if (!autoResize || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const computedStyle = window.getComputedStyle(textarea);

    const minHeight = Number.parseInt(computedStyle.minHeight, 10);
    const maxHeight = Number.parseInt(computedStyle.maxHeight, 10);

    textarea.style.height = `${minHeight}px`;
    const scrollHeight = textarea.scrollHeight;

    if (scrollHeight <= minHeight) {
      textarea.style.height = `${minHeight}px`;
    } else {
      const newHeight = Math.min(scrollHeight, maxHeight || scrollHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [autoResize]);

  useEffect(() => {
    if (autoResize) {
      resizeTextArea();
    }
  }, [value, autoResize, resizeTextArea]);

  // Initial resize on mount
  useEffect(() => {
    if (autoResize) {
      setTimeout(resizeTextArea, 0);
    }
  }, [autoResize, resizeTextArea]);

  // Function to perform the actual patchElement update
  const performPatchElement = useCallback(
    (newValue: string, previousValue: string) => {
      controller.patchElement(
        controller.id,
        (draft: TaskElement) => {
          draft[field] = newValue;
        },
        sequenceId
      );

      // Track field changed event
      onFieldChanged?.(field);

      // Track title set event (empty to non-empty) - only for title field
      if (field === "title") {
        const wasEmpty = !previousValue || previousValue.trim() === "";
        const isNowEmpty = !newValue || newValue.trim() === "";
        if (wasEmpty && !isNowEmpty) {
          controller.reportTitleSet("task-modal");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controller, field, onFieldChanged, sequenceId]
  );

  // Debounced function to update the element
  const debouncedPatchElement = useCallback(
    (newValue: string, previousValue: string) => {
      // Store pending update
      pendingUpdateRef.current = { newValue, previousValue };

      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        window.clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = window.setTimeout(() => {
        if (pendingUpdateRef.current) {
          performPatchElement(pendingUpdateRef.current.newValue, pendingUpdateRef.current.previousValue);
          pendingUpdateRef.current = null;
        }
        debounceTimeoutRef.current = 0;
      }, 500);
    },
    [performPatchElement]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      const previousValue = previousValueRef.current;
      setValue(newValue);

      // Use debounced update with sequence ID
      if (newValue.trim() !== previousValue.trim()) {
        debouncedPatchElement(newValue, previousValue);
      }

      previousValueRef.current = newValue;
    },
    [debouncedPatchElement]
  );

  // Stable cleanup function that uses refs to avoid dependency issues
  const cleanup = useCallback(async () => {
    // Flush any pending debounced update
    if (debounceTimeoutRef.current) {
      window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = 0;
    }

    // If there's a pending update, apply it immediately
    if (pendingUpdateRef.current) {
      const pendingUpdate = pendingUpdateRef.current;
      await controllerRef.current.patchElement(
        controllerRef.current.id,
        (draft: TaskElement) => {
          draft[fieldRef.current] = pendingUpdate.newValue;
        },
        sequenceId
      );

      // Track field changed event
      onFieldChangedRef.current?.(fieldRef.current);

      // Track title set event (empty to non-empty) - only for title field
      if (fieldRef.current === "title") {
        const wasEmpty = !pendingUpdate.previousValue || pendingUpdate.previousValue.trim() === "";
        const isNowEmpty = !pendingUpdate.newValue || pendingUpdate.newValue.trim() === "";
        if (wasEmpty && !isNowEmpty) {
          controllerRef.current.reportTitleSet("task-modal");
        }
      }

      previousValueRef.current = pendingUpdate.newValue;
      pendingUpdateRef.current = null;
    }

    // End the action sequence to create a single undo entry
    await controllerRef.current.endActionSequence(sequenceId);
  }, [sequenceId]); // Only sequenceId as dependency since it's stable

  const handleFocus = useCallback(() => {
    isEditingRef.current = true;
    controllerRef.current.startActionSequence(sequenceId);
  }, [sequenceId]);

  const handleBlur = useCallback(() => {
    isEditingRef.current = false;
    cleanup();
  }, [cleanup]);

  // Cleanup: end sequence and flush pending updates on unmount
  useEffect(() => {
    return () => {
      // Only cleanup if we're still editing (might have been cleaned up by blur already)
      if (isEditingRef.current) {
        cleanup();
      }
    };
  }, [cleanup]);

  return (
    <textarea
      ref={textareaRef}
      id={field}
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleTabInTextArea}
    />
  );
}
